import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import Icon from '../components/Icon';
import { useAuth } from '../context/AuthContext';
import { NAV_ITEMS } from './navigation';

/**
 * A group whose children expand beneath it.
 *
 * The group starts open when the current route is inside it, so arriving on a
 * sub-page never leaves its own link hidden. After that the user's toggling
 * wins, which is why the open state is held here rather than derived from the
 * route on every render.
 */
function NavGroup({ item, collapsed, onCloseMobile, can }) {
  const location = useLocation();
  const children = item.children.filter((c) => isVisible(can, c.permission));

  // A group whose landing page is one of its own children (Insight opens on
  // Provider) needs the section prefix, not the landing URL, or the second
  // child would leave the group looking unselected.
  const root = item.match || item.to;
  const isInside =
    location.pathname === root || location.pathname.startsWith(`${root}/`);

  const [open, setOpen] = useState(isInside);

  // Collapsed to icons there is no room for a sub-list, so the group behaves as
  // a plain link to its landing page.
  if (collapsed) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) => `nav-item ${isActive || isInside ? 'is-active' : ''}`}
        title={item.label}
        onClick={onCloseMobile}
      >
        <span className="nav-icon">
          <Icon name={item.icon} />
        </span>
      </NavLink>
    );
  }

  return (
    <div className="nav-group">
      <div className={`nav-group-head ${isInside ? 'is-inside' : ''}`}>
        {/* The label navigates and the chevron expands: one control doing both
            would force a page load just to see the children. */}
        <NavLink
          to={item.to}
          end
          className={({ isActive }) => `nav-item nav-group-link ${isActive ? 'is-active' : ''}`}
          onClick={() => {
            setOpen(true);
            onCloseMobile?.();
          }}
        >
          <span className="nav-icon">
            <Icon name={item.icon} />
          </span>
          <span>{item.label}</span>
        </NavLink>

        <button
          type="button"
          className="nav-group-toggle"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? 'Collapse' : 'Expand'} ${item.label}`}
        >
          <Icon name={open ? 'expand_less' : 'expand_more'} />
        </button>
      </div>

      {open && (
        <div className="nav-sublist">
          {children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              // `end` on the landing page only, or it would stay highlighted on
              // every child route beneath it.
              end={child.end}
              className={({ isActive }) => `nav-subitem ${isActive ? 'is-active' : ''}`}
              onClick={onCloseMobile}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Whether a nav entry should be shown.
 *
 * An entry's `permission` is usually one key, but a GROUP may list several — its children
 * can be different jobs held by different roles, and the group has to appear for anyone who
 * can reach any of them. An array means "any of these", not "all".
 *
 * Kept here rather than in `can()` because that helper is also what guards the routes
 * themselves, where "any of these" is the wrong default: a route protects one thing.
 */
function isVisible(can, permission) {
  if (!permission) return true;
  return Array.isArray(permission) ? permission.some((p) => can(p)) : can(permission);
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onCloseMobile }) {
  const { can } = useAuth();

  const visible = NAV_ITEMS.filter((item) => isVisible(can, item.permission));

  return (
    <aside className={`sidebar ${collapsed ? 'is-collapsed' : ''} ${mobileOpen ? 'is-open' : ''}`}>
      <div className="sidebar-head">
        <img className="sidebar-logo-img" src="/photos/logo.png" alt="My Desi Guru" />
        {!collapsed && (
          <div style={{ minWidth: 0 }}>
            <div className="sidebar-title">My Desi Guru</div>
            <div className="sidebar-subtitle">Super admin</div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav">
        {visible.map((item) =>
          item.children ? (
            <NavGroup
              key={item.to}
              item={item}
              collapsed={collapsed}
              onCloseMobile={onCloseMobile}
              can={can}
            />
          ) : (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}
              title={collapsed ? item.label : undefined}
              onClick={onCloseMobile}
            >
              <span className="nav-icon">
                <Icon name={item.icon} />
              </span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          )
        )}
      </nav>

      <div className="sidebar-foot">
        <button type="button" className="sidebar-toggle" onClick={onToggle}>
          {collapsed ? '›' : 'Collapse menu'}
        </button>
      </div>
    </aside>
  );
}
